import { useCallback } from 'react'

interface SingleRangeSliderProps {
  /** Absolute minimum value */
  min: number
  /** Absolute maximum value */
  max: number
  /** Step size */
  step: number
  /** Current value */
  value: number
  /** Called when the value changes */
  onChange: (v: number) => void
  /** Format a value for display */
  formatLabel?: (v: number) => string
  /** Unit label shown after the value text */
  unit?: string
  /** Label shown above the slider */
  label?: string
}

/**
 * A single-thumb range slider with a filled track from the left edge
 * to the current thumb position. Shows "Any" when at the minimum.
 */
export default function SingleRangeSlider({
  min,
  max,
  step,
  value,
  onChange,
  formatLabel,
  unit = '',
  label,
}: SingleRangeSliderProps) {
  const pct = ((value - min) / (max - min)) * 100
  const fmt = formatLabel ?? ((v: number) => String(v))
  const isDefault = value === min

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(Number(e.target.value))
    },
    [onChange],
  )

  return (
    <div className="space-y-2">
      {/* Header row: label + current value */}
      <div className="flex items-baseline justify-between">
        {label && (
          <span className="text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0]">
            {label}
          </span>
        )}
        <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
          {isDefault ? 'Any' : `≥ ${fmt(value)}${unit ? ` ${unit}` : ''}`}
        </span>
      </div>

      {/* Slider track + range input */}
      <div className="relative h-6 flex items-center">
        {/* Background track */}
        <div className="absolute inset-x-0 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700" />

        {/* Filled region from left to thumb */}
        <div
          className="absolute h-1.5 rounded-full bg-[#EF8354]"
          style={{ left: 0, right: `${100 - pct}%` }}
        />

        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          className="dual-range-thumb absolute inset-x-0 pointer-events-none appearance-none bg-transparent"
          style={{ zIndex: 3 }}
        />
      </div>

      {/* Min / Max labels */}
      <div className="flex justify-between text-[10px] text-zinc-400 dark:text-zinc-500 -mt-1">
        <span>{fmt(min)}{unit ? ` ${unit}` : ''}</span>
        <span>{fmt(max)}{unit ? ` ${unit}` : ''}</span>
      </div>
    </div>
  )
}
